import React, { useState, useMemo, useEffect, useRef, Suspense } from "react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
import { useDashboard } from "./dashboardStore";

import ReportExporter from "../utils/ReportExporter";
import StagingTable from "../utils/StagingTable";
import { socket } from "../utils/socket";
import FormattedNumberInput from "../utils/FormattedNumberInput";


const PurchaseOrderPDFForm = React.lazy(() =>
  import("../utils/PurchaseOrderPDFForm")
);

// Placeholders for modal inputs
const placeholdersForModal = {
	supplier_name: "Enter Supplier Name",
	item_name: "Enter Item / Reference",
	region: "Enter Region (e.g. Dubai)",
	total: "Enter Total value (eg. 20000)",
	default: "Enter value"
};

// Field labels for table/export
const fieldLabels = {
	supplier_name: "Supplier Name",
	item_name: "Reference Tag",
	payment_term: "Payment Term",
	availability: "Availability",
	total: "Total Price",
	region: "Region",
	status: "Status"
};

const PurchaseOrdersTable = () => {
	const [purchaseOrders, setPurchaseOrders] = useState([]);
	const [search, setSearch] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [newPO, setNewPO] = useState({
		supplier_name: "",
		item_name: "",
		payment_term: "",
		total: "",
		region: "",
		availability: "",
		status: "",
		created_at: ""
	});

	const [showPDFSection, setShowPDFSection] = useState(null);
	const [showStaging, setShowStaging] = useState(false);
	const [errors, setErrors] = useState({});
	const [page, setPage] = useState(1);
	const pageSize = 8;
	const [loading, setLoading] = useState(false);

	const parentRef = useRef(null);
	const stagingRef = useRef(null);

	// Scroll to staging table or main table
	useEffect(() => {
		if (showStaging && stagingRef.current) {
			stagingRef.current.scrollIntoView({ behavior: "smooth" });
		}
		if (!showStaging && parentRef?.current) {
			parentRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [showStaging]);

    // Socket listener for real-time updates
useEffect(() => {
    if (!socket) return;

    const handleNewPO = (newPO) => {
        setPurchaseOrders(prev => [newPO, ...prev]);
    };

    const handleDeletedPO = (deletedPO) => {
        setPurchaseOrders(prev => prev.filter(p => p.Id !== Number(deletedPO.Id)));
    };

    const handleBatchPO = (data) => {
        if (data.type === "batchPurchaseOrders") {
            setPurchaseOrders(prev => [...data.rows, ...prev]);
        }
    };

    socket.on("newPurchaseOrder", handleNewPO);
    socket.on("purchaseOrderDeleted", handleDeletedPO);
    socket.on("alertPurchaseOrders", handleBatchPO); // listen for batch

    return () => {
        socket.off("newPurchaseOrder", handleNewPO);
        socket.off("purchaseOrderDeleted", handleDeletedPO);
        socket.off("alertPurchaseOrders", handleBatchPO);
    };
}, [socket]);


// Subscribe to dashboard store slice
const poDat = useDashboard(state => state.data?.purchaseOrders);

// -------------------------------------
// Process mistakes requests data
// -------------------------------------
useEffect(() => {
  // Wait until store has data
  if (!poDat) return;

  try {
    // Save to state
			setPurchaseOrders(Array.isArray(poDat) ? poDat : []);
  } catch (err) {
    console.error("Error processing feature requests:", err);
    setItems([]);
  }
}, [poDat]); // Reactive: runs whenever store updates


	// Validation
	const validatePO = () => {
		const newErrors = {};
		Object.entries(newPO).forEach(([key, value]) => {
			if (key !== "created_at" && key !== "total" && !value) newErrors[key] = true;
		});
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Add new purchase order
	const handleAdd = async (po) => {
		if (!validatePO()) return;

		try {
			const res = await fetch(`${apiBaseUrl}/api/purchase-order`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					supplier_name: po.supplier_name,
					item_name: po.item_name,
					payment_term: po.payment_term,
					total: Number(po.total) || 0,
					region: po.region,
					availability: po.availability,
					status: po.status
				})
			});
			if (!res.ok) throw new Error("Failed to add purchase order");

			setShowModal(false);
			setNewPO({
				supplier_name: "",
				item_name: "",
				payment_term: "",
				total: "",
				region: "",
				availability: "",
				status: "",
				created_at: ""
			});
			setErrors({});
		} catch (err) {
			console.error(err);
			alert("Failed to add purchase order. Try again.");
		}
	};

	// Delete PO
	const handleDelete = async (id) => {
		const confirmDelete = window.confirm("⚠️ Are you sure you want to delete this purchase order?");
		if (!confirmDelete) return;   
		

		try {
			const res = await fetch(`${apiBaseUrl}/api/purchase-order/${id}`, { method: "DELETE" });
			if (!res.ok) throw new Error("Failed to delete PO");
		} catch (err) {
			console.error(err);
			alert("Failed to delete purchase order. Try again.");
		}
	};

	// Filter
	const filteredPOs = useMemo(() => {
		const safePOs = Array.isArray(purchaseOrders) ? purchaseOrders : [];
		const searchLower = search.toLowerCase();
		return safePOs.filter(p =>
			(p.supplier_name?.toLowerCase() ?? "").includes(searchLower) ||
			(p.item_name?.toLowerCase() ?? "").includes(searchLower) ||
			(p.payment_term?.toLowerCase() ?? "").includes(searchLower) ||
			(p.region?.toLowerCase() ?? "").includes(searchLower) ||
			(p.availability?.toLowerCase() ?? "").includes(searchLower) ||
			(p.status?.toLowerCase() ?? "").includes(searchLower)
		);
	}, [purchaseOrders, search]);

	const totalPages = Math.ceil((filteredPOs?.length || 0) / pageSize);
	const paginatedPOs = useMemo(() => {
		const start = (page - 1) * pageSize;
		return (filteredPOs || []).slice(start, start + pageSize);
	}, [filteredPOs, page]);
	
	


	return (
		<div className="w-full flex-1 overflow-y-auto p-4">


		
			{/* Top row: Used Regions & Exporter */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-5">
				<div className="p-2 border rounded-xl bg-white shadow-sm h-full max-w-4xl">
					<h2 className="text-lg font-semibold text-gray-800 mb-1">Used Regions</h2>
					<span className="text-xs text-gray-400 mb-3 block">
						Please use same letters as below or add new unit.
					</span>
					<div className="pt-4 flex flex-wrap gap-2">
						{[...new Set(purchaseOrders.map(i => i.region))].map((type, idx) => (
							<span key={idx} className="px-3 py-1 rounded-full bg-green-100 text-green-700 font-bold text-sm shadow-sm">
								{type}
							</span>
						))}
					</div>
				</div>

				<div className="h-full">
					<ReportExporter
						title="Purchase Orders Export"
						data={purchaseOrders}
						columns={[
							{ label: "Supplier Name", field: "supplier_name" },
							{ label: "Reference Tag", field: "item_name" },
							{ label: "Payment Term", field: "payment_term" },
							{ label: "Total", field: "total" },
							{ label: "Region", field: "region" },
							{ label: "Availability", field: "availability" },
							{ label: "Status", field: "status" },
							{ label: "VAT", field: "vat" },
						]}
						filterableFields={{
							supplier_name: "Supplier Name",
							item_name: "Reference Tag",
							payment_term: "Payment Term",
							status: "Status",
							availability: "Availability",
						}}
					/>
				</div>
			</div>

			{/* Search + Add PO + Batch Upload */}
			<div ref={parentRef} className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-2">
				<input
					type="text"
					placeholder="Search by supplier, item, payment term, region, availability or status..."
					className="px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200 flex-1"
					value={search}
					onChange={(e) => { setSearch(e.target.value); setPage(1); }}
				/>

				<button
					className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => setShowStaging(prev => !prev)}
				>
					Batch Upload
					<span className={`ml-2 transform transition-transform duration-200 ${showStaging ? "rotate-180" : "rotate-0"}`}>▼</span>
				</button>

				<button
					className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
					onClick={() => setShowModal(true)}
				>
					Add Purchase Order
				</button>
			</div>

			{/* Table */}
			<div className="overflow-x-auto rounded-md border border-gray-200">
				<table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-3 py-4 text-left font-medium text-gray-700">#</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Supplier Name</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Reference Tag</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Payment Term</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Region</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Availability</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Status</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Total</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">VAT (5%)</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Date Created</th>
							<th className="px-3 py-4 text-center font-medium text-gray-700">Actions</th>
						</tr>
					</thead>

					<tbody className="divide-y divide-gray-100">
						{paginatedPOs.length === 0 && (
							<tr>
								<td colSpan={11} className="px-3 py-4 text-center text-gray-500 italic">
									{loading ? "Loading..." : "No purchase orders found"}
								</td>
							</tr>
						)}

						{paginatedPOs.map((po, idx) => (
							<React.Fragment key={po.Id || idx}>
								<tr className="hover:bg-gray-300 even:bg-gray-200">
									<td className="px-3 py-2">{idx + 1}</td>
									<td className="px-3 py-2 text-center">{po.supplier_name}</td>
									<td className="px-3 py-2 text-center">{po.item_name}</td>
									<td className="px-3 py-2 text-center">{po.payment_term}</td>

									<td className="px-3 py-2 text-center">{po.region}</td>
									<td className="px-3 py-2 text-center">{po.availability}</td>
									<td className={`px-3 py-2 text-center ${po.status === "Completed" ? "text-green-600" : po.status === "Pending" ? "text-yellow-600" : ""}`}>
										{po.status}
									</td>

        <td className="px-3 py-2 text-right">
          {Number(po.total || 0) === 0 ? (
            <span className="bg-yellow-600 text-white p-1 rounded">Add items</span>
          ) : (
            `${Number(po.total).toLocaleString()} AED`
          )}
        </td>

									            <td className="px-3 py-2 text-center">
                                                  {Number(po.total || 0) === 0 ? (
                                                    <span className="bg-yellow-600 text-white p-1 rounded">Click PDF</span>
                                                  ) : (
                                                    `${(po.total * 0.05).toLocaleString()} AED`
                                                  )}
                                                </td>
									
									<td className="px-3 py-2 text-center">
										{new Date(po.created_at).toLocaleDateString("en-GB", {
											day: "numeric",
											month: "long",
											year: "numeric"
										})}
									</td>
									<td className="px-3 py-2 text-center">
										<button
											className="px-2 py-1 mb-1 text-sm text-white bg-yellow-600 rounded-md hover:bg-yellow-700 cursor-pointer mr-1"
											onClick={() => setShowPDFSection(prev => (prev === idx ? null : idx))}
										>
											PDF
										</button>
										<button
											className="px-2 py-1 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 cursor-pointer"
											onClick={() => handleDelete(po.Id)}
										>
											Delete
										</button>
									</td>
								</tr>

								{/* PDF Section */}
								{showPDFSection === idx && (
									<Suspense fallback={null}>

									<PurchaseOrderPDFForm
										purchaseOrder={po}
										onClose={() => setShowPDFSection(null)}

										onPOUpdate={(updatedTotal) => {
											setPurchaseOrders(prev =>
												prev.map(item =>
													item.Id === po.Id ? { ...item, total: updatedTotal } : item
												)
											);
										}}
									/>
									</Suspense>
								)}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="flex items-center justify-center gap-2 mt-5">
					<button
						className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
						onClick={() => setPage(p => Math.max(p - 1, 1))}
						disabled={page === 1}
					>Prev</button>
					<span className="text-sm text-gray-700">Page {page} of {totalPages}</span>
					<button
						className="px-3 py-1 text-sm rounded-md border disabled:opacity-50 cursor-pointer"
						onClick={() => setPage(p => Math.min(p + 1, totalPages))}
						disabled={page === totalPages}
					>Next</button>
				</div>
			)}

			{/* Modal */}
			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="bg-white w-full max-w-md rounded-md shadow-lg p-6 relative">
						<h2 className="text-lg font-semibold mb-4">Add New Purchase Order</h2>
						<div className="flex flex-col gap-3">
							{Object.keys(newPO).map((field) => {
								if (field === "created_at" || field === "total") return null;

								// Select fields
								if (field === "status") {
									return (
										<select
											key={field}
											className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors.status ? "border-red-500" : "border-gray-300"}`}
											value={newPO.status}
											onChange={(e) => setNewPO({ ...newPO, status: e.target.value })}
										>
											<option value="">Select Status</option>
											<option value="Pending">Pending</option>
											<option value="Completed">Completed</option>
										</select>
									);
								}

								if (field === "payment_term") {
									return (
										<select
											key={field}
											className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors.payment_term ? "border-red-500" : "border-gray-300"}`}
											value={newPO.payment_term}
											onChange={(e) => setNewPO({ ...newPO, payment_term: e.target.value })}
										>
											<option value="">Select Payment Term</option>
											<option value="30 Days PDC">30 Days PDC</option>
											<option value="60 Days PDC">60 Days PDC</option>
											<option value="120 Days PDC">120 Days PDC</option>
											<option value="Cash on Delivery">Cash on Delivery</option>
											<option value="Bank Transfer">Bank Transfer</option>
										</select>
									);
								}

								if (field === "availability") {
									return (
										<select
											key={field}
											className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors.availability ? "border-red-500" : "border-gray-300"}`}
											value={newPO.availability}
											onChange={(e) => setNewPO({ ...newPO, availability: e.target.value })}
										>
											<option value="">Select Availability</option>
											<option value="Available">Available</option>
											<option value="Not Available">Not Available</option>
										</select>
									);
								}

								// Default input
								return (
									<input
										key={field}
										type="text"
										placeholder={placeholdersForModal[field] || placeholdersForModal.default}
										className={`px-3 py-2 border rounded-md focus:outline-none focus:ring focus:ring-blue-200 ${errors[field] ? "border-red-500" : "border-gray-300"}`}
										value={newPO[field]}
										onChange={(e) => setNewPO({ ...newPO, [field]: e.target.value })}
									/>
								);
							})}
						</div>

						{/* Modal Actions */}
						<div className="flex justify-end gap-2 mt-4">
							<button className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer" onClick={() => { setShowModal(false); setErrors({}); }}>Cancel</button>
							<button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer" onClick={() => handleAdd(newPO)}>Add</button>
						</div>

						{/* Close icon */}
						<button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer" onClick={() => { setShowModal(false); setErrors({}); }}>✕</button>
					</div>
				</div>
			)}

			{/* Staging Table */}
			<StagingTable
				ref={stagingRef}
				visible={showStaging}
				setVisible={setShowStaging}
				apiProp={`${apiBaseUrl}/api/purchase-order/batch`}
				entryDict={Object.fromEntries(
					Object.keys(newPO)
						.filter(key => key !== "created_at" && key !== "total")
						.map(key => {
							let type = "input";
							let options;
							const label = fieldLabels[key];
							let additionalTypes = null;
							let backendKey = key;

							if (key === "supplier_name") backendKey = "supplier_name";
							if (key === "item_name") backendKey = "item_name";
							if (key === "payment_term") {
								backendKey = "payment_term";
								type = "select";
								options = ["30 Days PDC", "60 Days PDC", "120 Days PDC", "Cash on Delivery", "Bank Transfer"];
							}
							if (key === "availability") {
								type = "select";
								options = ["Available", "Not Available"];
							}
							if (key === "status") {
								type = "select";
								options = ["Pending", "Completed"];
							}
							if (key === "total") additionalTypes = "number";

							return [backendKey, { type, options, label, additionalTypes }];
						})
				)}
			/>
		</div>
	);
};

export default PurchaseOrdersTable;
